// ─── Trailing Stop Session Persistence ──────────────────────
// Saves trailing stop config immediately to Supabase for cross-device
// session restore. localStorage is used as fast fallback only.
// Pattern mirrors useBotSessionPersistence / useChinWorkspacePersistence.
//
// Each Spotware account has its own independent session stored
// under a composite key: {strategyId}__account_{accountId}.
//
// CROSS-DEVICE SYNC:
//   Besides saving, this hook now LISTENS (Supabase Realtime) for
//   changes to this strategy+account session made from OTHER devices.
//   When another device changes settings, it notifies the caller via
//   onExternalChange so the engine/UI update WITHOUT a manual refresh.
//   A per-device writerId skips our own writes echoing back.

import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  getTrailingEngine,
  type TrailingStopParams,
  normalizeTrailingParams,
} from '@/lib/trailing-stop-engine';
import { buildSessionKey } from '@/lib/session-key';
import { getDeviceWriterId } from '@/lib/device-id';

// ─── Types ──────────────────────────────────────────────────

export interface TrailingSessionData {
  selectedAccountId: string;
  localParams: TrailingStopParams;
  wasRunning: boolean;
  lastActive: number;
}

// ─── LocalStorage key ───────────────────────────────────────

function lsKey(strategyId: string, accountId: string): string {
  return accountId
    ? `trailing_session__${strategyId}__account_${accountId}`
    : `trailing_session__${strategyId}`;
}

// ─── Hook ───────────────────────────────────────────────────

export function useTrailingSessionPersistence(
  strategyId: string,
  accountId: string,
  onExternalChange?: (params: TrailingStopParams) => void,
) {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionKey = accountId ? buildSessionKey(strategyId, accountId) : strategyId;

  // Keep the latest external-change callback in a ref so the realtime
  // subscription below never holds a stale closure.
  const onExternalChangeRef = useRef(onExternalChange);
  onExternalChangeRef.current = onExternalChange;

  // ─── Save to Supabase ────────────────────────────────────

  const saveToSupabase = useCallback(async (data: TrailingSessionData) => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;

      await supabase
        .from('bot_session_state')
        .upsert({
          user_id: authData.user.id,
          strategy_id: sessionKey,
          session_data: {
            type: 'trailing_stop',
            writerId: getDeviceWriterId(),
            selectedAccountId: data.selectedAccountId,
            localParams: data.localParams,
            wasRunning: data.wasRunning,
            lastActive: data.lastActive,
          } as unknown as Record<string, unknown>,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,strategy_id' });
    } catch (err) {
      console.warn('[TrailingPersistence] Failed to save to Supabase:', err);
    }
  }, [sessionKey]);

  // ─── Save to localStorage (backup) ───────────────────────

  const saveToLocalStorage = useCallback((data: TrailingSessionData) => {
    try {
      localStorage.setItem(lsKey(strategyId, accountId), JSON.stringify(data));
    } catch { /* ignore */ }
  }, [strategyId, accountId]);

  // ─── Collect current state from engine ───────────────────

  const collectSessionData = useCallback((): TrailingSessionData | null => {
    const engine = getTrailingEngine(strategyId, accountId);
    if (!engine) return null;

    return {
      selectedAccountId: engine.selectedAccountId,
      localParams: engine.currentParams,
      wasRunning: engine.isRunning,
      lastActive: Date.now(),
    };
  }, [strategyId, accountId]);

  // ─── Schedule save (debounced Supabase, immediate localStorage) ─

  const scheduleSave = useCallback((params: TrailingStopParams) => {
    const np = normalizeTrailingParams(params);

    const data: TrailingSessionData = {
      selectedAccountId: getTrailingEngine(strategyId, accountId)?.selectedAccountId || '',
      localParams: np,
      wasRunning: getTrailingEngine(strategyId, accountId)?.isRunning || false,
      lastActive: Date.now(),
    };

    // localStorage immediately (sync, zero cost)
    saveToLocalStorage(data);

    // Debounce Supabase (batches rapid changes, latest wins in 500ms)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const latest = collectSessionData();
      if (latest) saveToSupabase(latest);
    }, 500);
  }, [strategyId, accountId, saveToSupabase, saveToLocalStorage, collectSessionData]);

  // ─── Immediate save (for critical moments) ──────────────

  const saveImmediate = useCallback((params: TrailingStopParams) => {
    const data = collectSessionData();
    if (data) {
      saveToLocalStorage(data);
      saveToSupabase(data);
    }
  }, [collectSessionData, saveToSupabase, saveToLocalStorage]);

  // ─── Load from Supabase ──────────────────────────────────

  const loadFromSupabase = useCallback(async (): Promise<TrailingSessionData | null> => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return null;

      // Try composite key first
      let { data, error } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', authData.user.id)
        .eq('strategy_id', sessionKey)
        .maybeSingle();

      // Fallback: try old key (just strategyId) for migration
      if ((error || !data?.session_data) && accountId) {
        const fallback = await supabase
          .from('bot_session_state')
          .select('session_data')
          .eq('user_id', authData.user.id)
          .eq('strategy_id', strategyId)
          .maybeSingle();

        if (!fallback.error && fallback.data?.session_data) {
          data = fallback.data;
          error = fallback.error;
        }
      }

      if (error || !data?.session_data) return null;

      const raw = data.session_data as Record<string, unknown>;
      if (raw.type !== 'trailing_stop') return null;

      return {
        selectedAccountId: (raw.selectedAccountId as string) || '',
        localParams: normalizeTrailingParams(raw.localParams as Partial<TrailingStopParams> | null | undefined),
        wasRunning: !!raw.wasRunning,
        lastActive: (raw.lastActive as number) || 0,
      };
    } catch {
      return null;
    }
  }, [sessionKey, strategyId, accountId]);

  // ─── Load from localStorage ─────────────────────────────

  const loadFromLocalStorage = useCallback((): TrailingSessionData | null => {
    try {
      const raw = localStorage.getItem(lsKey(strategyId, accountId));
      if (!raw) {
        // Fallback: try old key
        const oldRaw = localStorage.getItem(lsKey(strategyId, ''));
        if (!oldRaw) return null;
        const parsed = JSON.parse(oldRaw) as TrailingSessionData;
        if (!parsed.lastActive) return null;
        return { ...parsed, localParams: normalizeTrailingParams(parsed.localParams) };
      }
      const parsed = JSON.parse(raw) as TrailingSessionData;
      if (!parsed.lastActive) return null;
      return { ...parsed, localParams: normalizeTrailingParams(parsed.localParams) };
    } catch {
      return null;
    }
  }, [strategyId, accountId]);

  // ─── Load with fallback (compare timestamps) ──────────────

  const loadSessionWithFallback = useCallback(async (): Promise<TrailingSessionData | null> => {
    const [supabaseData, localData] = await Promise.all([
      loadFromSupabase(),
      Promise.resolve(loadFromLocalStorage()),
    ]);

    if (!supabaseData && !localData) return null;

    if (!supabaseData) {
      if (localData) saveToSupabase(localData);
      return localData;
    }
    if (!localData) return supabaseData;

    const supabaseTs = supabaseData.lastActive || 0;
    const localTs = localData.lastActive || 0;

    if (localTs > supabaseTs) {
      console.log('[TrailingPersistence] localStorage newer, syncing to Supabase');
      saveToSupabase(localData);
      return localData;
    }

    return supabaseData;
  }, [loadFromSupabase, loadFromLocalStorage, saveToSupabase]);

  // ─── Realtime cross-device sync ─────────────────────────
  // Listens for changes to this strategy+account session from OTHER
  // devices. When another device saves, read the latest params from
  // the payload and notify the caller so the engine applies them live.
  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    const myWriterId = getDeviceWriterId();

    async function setup() {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId || cancelled) return;

      const handleChange = (payload: {
        new?: { strategy_id?: string; session_data?: Record<string, unknown> };
      }) => {
        const row = payload.new;
        if (!row || row.strategy_id !== sessionKey) return;
        const sd = row.session_data;
        if (!sd || sd.type !== 'trailing_stop') return;
        if (sd.writerId === myWriterId) return; // our own echo — ignore

        const np = normalizeTrailingParams(sd.localParams as Partial<TrailingStopParams> | null | undefined);
        console.log('[TrailingPersistence] 🔄 Realtime change from another device');
        onExternalChangeRef.current?.(np);
      };

      channel = supabase
        .channel(`trailing-sync-${sessionKey}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bot_session_state',
            filter: `user_id=eq.${userId}`,
          },
          handleChange,
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'bot_session_state',
            filter: `user_id=eq.${userId}`,
          },
          handleChange,
        )
        .subscribe();
    }

    setup();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [sessionKey]);

  // ─── beforeunload: keepalive save to Supabase ────────────

  useEffect(() => {
    const handler = () => {
      const data = collectSessionData();
      if (!data) return;

      saveToLocalStorage(data);

      try {
        const supabaseUrl = (import.meta as Record<string, unknown>).env?.VITE_PUBLIC_SUPABASE_URL as string;
        const supabaseKey = (import.meta as Record<string, unknown>).env?.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
        if (supabaseUrl && supabaseKey) {
          let authHeader = '';
          let userId = '';
          try {
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.endsWith('-auth-token')) {
                const raw = localStorage.getItem(key);
                if (raw) {
                  const parsed = JSON.parse(raw);
                  if (parsed.access_token) authHeader = `Bearer ${parsed.access_token}`;
                  if (parsed.user?.id) userId = parsed.user.id;
                  break;
                }
              }
            }
          } catch { /* ignore */ }

          if (authHeader && userId) {
            fetch(`${supabaseUrl}/rest/v1/bot_session_state`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': supabaseKey,
                'Authorization': authHeader,
                'Prefer': 'resolution=merge-duplicates',
              },
              body: JSON.stringify({
                user_id: userId,
                strategy_id: sessionKey,
                session_data: {
                  type: 'trailing_stop',
                  writerId: getDeviceWriterId(),
                  selectedAccountId: data.selectedAccountId,
                  localParams: data.localParams,
                  wasRunning: data.wasRunning,
                  lastActive: data.lastActive,
                },
                updated_at: new Date().toISOString(),
              }),
              keepalive: true,
            }).catch(() => { /* best-effort */ });
          }
        }
      } catch { /* best-effort */ }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [strategyId, accountId, sessionKey, collectSessionData, saveToLocalStorage]);

  // ─── Periodic backup to Supabase (5-min heartbeat) ──────

  useEffect(() => {
    const interval = setInterval(() => {
      const data = collectSessionData();
      if (data && data.selectedAccountId) {
        saveToSupabase(data);
      }
    }, 300000);

    return () => clearInterval(interval);
  }, [strategyId, collectSessionData, saveToSupabase]);

  // ─── Cleanup debounce timer on unmount ──────────────────

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return {
    scheduleSave,
    saveImmediate,
    loadSessionWithFallback,
  };
}